import {
  ElevatedSurface,
  IconButton,
  NumberInput,
  RotateCwIcon,
  SingleSelectDropdown,
  Spinner,
  TextInput,
  toast,
  TooltipIconButton,
} from "@carbon6/c6-frontend-design";
import { useEffect, useState } from "react";
import { StatusChip } from "@/shared/ui/status";
import { formatDateTime } from "@/shared/utils/datetime/format";
import { textOrNull } from "@/shared/utils/text";
import {
  FACT_BASES,
  FACT_GROUPS,
  FACT_INPUTS,
  FACT_PATHS,
  eventChangedPaths,
  factGroupOf,
  factIssues,
  isEmptyFact,
  sameFact,
  type AnyFact,
  type DatasetFactsRecord,
  type FactBasis,
  type FactField,
  type FactGroup,
  type FactIssue,
  type FactPath,
} from "../../domain/datasetFacts";
import {
  canConfirm,
  canDiscard,
  canPropose,
  type DatasetFactsEditor,
  type DatasetFactsEditorState,
} from "../../feature/datasetFactsEditor";
import {
  FACT_ACTION_LABEL,
  FACT_BASIS_LABEL,
  FACT_CLIENT_LABEL,
  FACT_GROUP_LABEL,
  FACT_PATH_LABEL,
  FACT_UNKNOWN_LABEL,
  formatFactChoice,
  formatFactIssue,
  formatFactValue,
} from "../../feature/datasetFactsFormat";
import { useDatasetFactsEditor } from "../useDatasetFactsEditor";
import cardStyles from "./DetailCard.module.css";
import styles from "./DatasetFactsSection.module.css";

type DatasetFactsSectionProps = {
  datasetUuid: string;
  /** 창이 보이는지. 숨은 동안에는 서버를 다시 묻지 않는다. */
  active: boolean;
};

/**
 * `조사 사실` 섹션 — Dataset 마다 미리 조사한 사실의 active 와 편집(candidate)을 칸마다 나란히 보인다.
 *
 * 편집은 candidate 가 있으면 그 값에서 시작한다. `제안` 은 candidate 로 올리고, `확정` 은 편집본을 active 로 올린다.
 * 자료가 없는 칸은 "자료 없음 — 직접 조사" 다. 섹션은 Probe 가능 여부와 관계없이 열린다.
 */
export function DatasetFactsSection({ active, datasetUuid }: DatasetFactsSectionProps) {
  const { editor, state } = useDatasetFactsEditor(datasetUuid, active);
  const { server } = state;

  if (server.status === "loading") {
    return (
      <ElevatedSurface
        as="section"
        className={cardStyles.card}
        elevation={1}
        hoverElevation={false}
      >
        <Spinner label="조사 사실을 불러오는 중" size="md" />
      </ElevatedSurface>
    );
  }

  if (server.status === "failed") {
    return (
      <ElevatedSurface
        as="section"
        className={cardStyles.card}
        elevation={1}
        hoverElevation={false}
      >
        <p className={styles.error} role="alert">
          {server.message}
        </p>
        <IconButton text="다시 시도" variant="surface" onClick={() => void editor.load()}>
          <RotateCwIcon />
        </IconButton>
      </ElevatedSurface>
    );
  }

  return (
    <>
      <FactsEditorCard editor={editor} record={server.record} state={state} />
      <FactsHistoryCard editor={editor} revision={server.record.revision} state={state} />
    </>
  );
}

function FactsEditorCard({
  editor,
  record,
  state,
}: {
  editor: DatasetFactsEditor;
  record: DatasetFactsRecord;
  state: DatasetFactsEditorState;
}) {
  const issueList = factIssues(state.draft);
  const issues = new Map(issueList.map((issue) => [issue.path, issue]));
  const busy = state.pending !== null;
  const { candidate } = record;

  const run = async (command: () => Promise<boolean>, success: string): Promise<void> => {
    if (await command()) {
      toast.success(success);
    }
  };

  return (
    <ElevatedSurface as="section" className={cardStyles.card} elevation={1} hoverElevation={false}>
      <h2 className={cardStyles.title}>조사 사실</h2>
      <p className={styles.lead}>
        변환 전에 확인한 이 Dataset 의 사실입니다. MCP 와 이 화면이 같은 값을 읽습니다. active 가
        &quot;{FACT_UNKNOWN_LABEL}&quot; 인 칸은 자료가 없으니 직접 조사해 주세요.
      </p>
      {candidate === null ? null : (
        <p className={styles.candidate} role="status">
          {FACT_CLIENT_LABEL[candidate.client]} 에서 {formatDateTime(candidate.modifiedAtMs)} 에
          제안한 candidate 가 있습니다(제안 Member {candidate.proposedBy.slice(0, 8)}). 편집 칸은 이
          candidate 에서 시작하고, 확정하면 active 가 됩니다.
        </p>
      )}
      {state.refreshError === null ? null : (
        <p className={styles.error} role="status">
          서버 값을 다시 확인하지 못했습니다. {state.refreshError}
        </p>
      )}
      {FACT_GROUPS.map((group) => (
        <FactGroupTable
          key={group}
          disabled={busy}
          editor={editor}
          group={group}
          issues={issues}
          record={record}
          state={state}
        />
      ))}
      <div className={styles.actions}>
        <ActionButton
          blockedReason={
            canPropose(state, issueList) ? null : proposeBlockedReason(state, issueList.length)
          }
          busy={busy}
          text={state.pending === "propose" ? "제안 중" : "제안"}
          tooltip="편집본을 candidate 로 올립니다. active 는 그대로입니다."
          variant="surface"
          onClick={() =>
            void run(
              () => editor.propose(),
              "candidate 로 제안했습니다. 확정하면 active 가 됩니다.",
            )
          }
        />
        <ActionButton
          blockedReason={
            canConfirm(state, issueList) ? null : confirmBlockedReason(issueList.length)
          }
          busy={busy}
          text={state.pending === "confirm" ? "확정 중" : "확정"}
          tooltip="편집본을 active 로 올리고 candidate 를 지웁니다."
          variant="solid"
          onClick={() => void run(() => editor.confirm(), "조사 사실을 확정했습니다.")}
        />
        <ActionButton
          blockedReason={canDiscard(state) ? null : discardBlockedReason(state, record)}
          busy={busy}
          text={state.pending === "discard" ? "폐기 중" : "제안 폐기"}
          tone="danger"
          tooltip="candidate 를 지웁니다. active 는 그대로입니다."
          variant="surface"
          onClick={() => void run(() => editor.discard(), "candidate 를 폐기했습니다.")}
        />
        <IconButton
          disabled={busy || !state.dirty}
          text="편집 취소"
          tone="neutral"
          type="button"
          variant="surface"
          onClick={() => editor.revert()}
        />
        {state.dirty ? <span className={styles.dirty}>저장하지 않은 편집이 있습니다.</span> : null}
      </div>
      <p className={cardStyles.caption}>바꾼 기록은 아래 변경 이력에 남습니다.</p>
      {state.commandError === null ? null : (
        <p className={styles.error} role="alert">
          {state.commandError}
        </p>
      )}
    </ElevatedSurface>
  );
}

/**
 * 쓰기 버튼. 툴팁은 늘 둔다 — 막혔을 때만 감싸면 상태가 바뀔 때 버튼 노드가 바뀌어 초점을 잃는다. 막혔으면 툴팁이
 * 사유를 말한다. 요청 중에는 사유를 말하지 않는다(곧 풀린다).
 */
function ActionButton({
  blockedReason,
  busy,
  onClick,
  text,
  tone,
  tooltip,
  variant,
}: {
  blockedReason: string | null;
  busy: boolean;
  text: string;
  tone?: "danger";
  tooltip: string;
  variant: "surface" | "solid";
  onClick: () => void;
}) {
  return (
    <TooltipIconButton
      aria-label={text}
      disabled={blockedReason !== null}
      disabledTooltip={busy ? tooltip : (blockedReason ?? undefined)}
      text={text}
      tone={tone}
      tooltip={tooltip}
      type="button"
      variant={variant}
      onClick={onClick}
    >
      {null}
    </TooltipIconButton>
  );
}

function proposeBlockedReason(state: DatasetFactsEditorState, issueCount: number): string {
  if (!state.dirty) return "제안할 편집이 없습니다. 칸을 바꾼 뒤 제안해 주세요.";
  return issueCount > 0 ? "표시된 칸을 먼저 고쳐 주세요." : "지금은 제안할 수 없습니다.";
}

function confirmBlockedReason(issueCount: number): string {
  return issueCount > 0
    ? "표시된 칸을 먼저 고쳐 주세요."
    : "확정할 변경이 없습니다. 편집본이 active 와 같고 candidate 도 없습니다.";
}

function discardBlockedReason(state: DatasetFactsEditorState, record: DatasetFactsRecord): string {
  if (record.candidate === null) return "폐기할 candidate 가 없습니다.";
  return state.dirty
    ? "저장하지 않은 편집이 있습니다. 먼저 편집을 취소하거나 제안해 주세요."
    : "지금은 폐기할 수 없습니다.";
}

function FactGroupTable({
  disabled,
  editor,
  group,
  issues,
  record,
  state,
}: {
  disabled: boolean;
  editor: DatasetFactsEditor;
  group: FactGroup;
  issues: ReadonlyMap<FactPath, FactIssue>;
  record: DatasetFactsRecord;
  state: DatasetFactsEditorState;
}) {
  const paths = FACT_PATHS.filter((path) => factGroupOf(path) === group);
  return (
    <table className={styles.table}>
      <caption className={styles.caption}>{FACT_GROUP_LABEL[group]}</caption>
      <thead>
        <tr>
          <th scope="col">칸</th>
          <th scope="col">active</th>
          <th scope="col">편집(candidate)</th>
        </tr>
      </thead>
      <tbody>
        {paths.map((path) => {
          const changed = !sameFact(state.draft[path], record.active[path]);
          const issue = issues.get(path);
          return (
            <tr key={path} data-changed={changed ? "true" : "false"}>
              <th scope="row" className={styles.field}>
                <div className={styles.fieldBody}>
                  <span>{FACT_PATH_LABEL[path]}</span>
                  <code className={styles.path}>{path}</code>
                  {changed ? <StatusChip tone="muted">active 와 다름</StatusChip> : null}
                </div>
              </th>
              <td className={styles.cell}>
                <FactView fact={record.active[path]} />
              </td>
              <td className={styles.cell}>
                <FactInputs
                  disabled={disabled}
                  fact={state.draft[path]}
                  invalid={issue?.fields ?? []}
                  path={path}
                  onChange={(fact) => editor.edit(path, fact)}
                />
                {issue === undefined ? null : (
                  <p className={styles.issue}>{formatFactIssue(issue.code)}</p>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** 칸 하나의 읽기 표시 — 값 · 근거 · 출처 메모. 자료가 없으면 다음 행동을 말한다. */
function FactView({ fact }: { fact: AnyFact }) {
  if (isEmptyFact(fact)) {
    return <span className={styles.unknown}>{FACT_UNKNOWN_LABEL}</span>;
  }
  return (
    <span className={styles.fact}>
      <span className={styles.value}>{formatFactValue(fact)}</span>
      {fact.basis === null ? null : (
        <span className={styles.basis}>{FACT_BASIS_LABEL[fact.basis]}</span>
      )}
      {fact.note === null ? null : <span className={styles.note}>{fact.note}</span>}
    </span>
  );
}

/**
 * 이름 입력의 `maxLength`. 상한은 글자(code point) 수인데 `maxLength` 는 UTF-16 단위를 센다 — 이모지 하나가 두
 * 단위다. 그대로 두면 맞는 값을 자르므로 두 배를 둔다. 넘친 글자는 검사(`text_too_long`)가 말한다.
 */
function utf16LimitOf(codePoints: number): number {
  return codePoints * 2;
}

/**
 * 칸 하나의 세 입력. `invalid` 는 검사 문제가 가리키는 입력이다. c6 드롭다운에는 invalid 표시가 없어 값 · 근거
 * 드롭다운은 표시하지 않는다 — 줄 아래 문장이 고칠 곳을 말한다.
 */
function FactInputs({
  disabled,
  fact,
  invalid,
  onChange,
  path,
}: {
  disabled: boolean;
  fact: AnyFact;
  invalid: readonly FactField[];
  path: FactPath;
  onChange: (fact: AnyFact) => void;
}) {
  const label = FACT_PATH_LABEL[path];
  const input = FACT_INPUTS[path];
  const setValue = (value: string | number | null) => onChange({ ...fact, value });
  return (
    <div className={styles.inputs}>
      {input.kind === "choice" ? (
        <SingleSelectDropdown
          disabled={disabled}
          fullWidth
          label={`${label} 값`}
          options={[
            { value: "", label: "자료 없음" },
            ...input.options.map((option) => ({ value: option, label: choiceLabel(option) })),
          ]}
          value={typeof fact.value === "string" ? fact.value : ""}
          onValueChange={(next) => setValue(next === "" ? null : next)}
        />
      ) : input.kind === "number" ? (
        <NumberInput
          aria-label={`${label} 값`}
          disabled={disabled}
          invalid={invalid.includes("value")}
          mode="decimal"
          placeholder="자료 없음"
          value={typeof fact.value === "number" ? fact.value : null}
          onChange={(value) => setValue(value)}
        />
      ) : (
        <TextInput
          aria-label={`${label} 값`}
          disabled={disabled}
          invalid={invalid.includes("value")}
          maxLength={utf16LimitOf(input.maxLength)}
          placeholder="자료 없음"
          value={typeof fact.value === "string" ? fact.value : ""}
          onChange={(event) => setValue(textOrNull(event.target.value))}
        />
      )}
      <SingleSelectDropdown
        disabled={disabled}
        fullWidth
        label={`${label} 근거`}
        options={[
          { value: "", label: "근거 없음" },
          ...FACT_BASES.map((basis) => ({ value: basis, label: FACT_BASIS_LABEL[basis] })),
        ]}
        value={fact.basis ?? ""}
        onValueChange={(next) =>
          onChange({ ...fact, basis: next === "" ? null : (next as FactBasis) })
        }
      />
      <TextInput
        aria-label={`${label} 출처 메모`}
        className={styles.noteInput}
        disabled={disabled}
        invalid={invalid.includes("note")}
        placeholder="출처 · 조사 범위(예: ep0–2 실측, ZERG-945)"
        value={fact.note ?? ""}
        onChange={(event) => onChange({ ...fact, note: textOrNull(event.target.value) })}
      />
    </div>
  );
}

/** 어휘 선택지 — 한국어 이름과 wire 값을 함께 보인다. 축 · 순서 · 단위는 값이 이미 이름이다. */
function choiceLabel(option: string): string {
  const name = formatFactChoice(option);
  return name === option ? option : `${name} (${option})`;
}

function FactsHistoryCard({
  editor,
  revision,
  state,
}: {
  editor: DatasetFactsEditor;
  revision: number;
  state: DatasetFactsEditorState;
}) {
  const [open, setOpen] = useState(false);
  // 펼친 동안 revision 이 바뀌면(제안 · 확정 · 다른 곳의 변경) 다시 받는다.
  useEffect(() => {
    if (open) void editor.loadHistory();
  }, [editor, open, revision]);
  const { history } = state;

  return (
    <ElevatedSurface as="section" className={cardStyles.card} elevation={1} hoverElevation={false}>
      <details onToggle={(event) => setOpen(event.currentTarget.open)}>
        <summary className={styles.historySummary}>변경 이력</summary>
        {history.status === "loading" || history.status === "idle" ? (
          <Spinner label="변경 이력을 불러오는 중" size="sm" />
        ) : history.status === "failed" ? (
          <p className={styles.error} role="alert">
            {history.message}
          </p>
        ) : history.events.length === 0 ? (
          <p className={cardStyles.empty}>아직 바뀐 기록이 없습니다.</p>
        ) : (
          <ol className={styles.history} aria-label="조사 사실 변경 이력">
            {history.events.map((event) => {
              const paths = eventChangedPaths(event);
              return (
                <li key={event.uuid}>
                  <span className={styles.historyHead}>
                    {formatDateTime(event.createdAtMs)} · {FACT_ACTION_LABEL[event.action]} ·{" "}
                    {FACT_CLIENT_LABEL[event.client]} · Revision {event.revision}
                  </span>
                  {paths.length === 0 ? null : (
                    <span className={styles.historyPaths}>
                      바뀐 칸: {paths.map((path) => FACT_PATH_LABEL[path]).join(", ")}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        )}
        <p className={cardStyles.caption}>
          되돌리려면 이력의 이전 값을 편집 칸에 다시 적고 확정해 주세요.
        </p>
      </details>
    </ElevatedSurface>
  );
}

type DatasetFactsSummaryProps = {
  datasetUuid: string;
  active: boolean;
};

/**
 * 점검 화면에 두는 active 요약(읽기 전용). 편집은 `조사 사실` 섹션이 한다. 같은 편집 상태를 보므로 섹션을 오가도
 * 다시 받지 않는다.
 */
export function DatasetFactsSummary({ active, datasetUuid }: DatasetFactsSummaryProps) {
  const { state } = useDatasetFactsEditor(datasetUuid, active);
  const { server } = state;
  if (server.status === "loading") {
    return <p className={styles.summaryNote}>조사 사실을 불러오는 중입니다.</p>;
  }
  if (server.status === "failed") {
    return <p className={styles.summaryNote}>{server.message}</p>;
  }
  const { active: facts, candidate } = server.record;
  return (
    <>
      <dl className={styles.summary} aria-label="조사 사실 요약">
        {FACT_PATHS.map((path) => {
          const fact = facts[path];
          return (
            <div key={path} className={styles.summaryRow}>
              <dt>{FACT_PATH_LABEL[path]}</dt>
              <dd data-unknown={isEmptyFact(fact) ? "true" : "false"}>
                {formatFactValue(fact)}
                {fact.basis === null ? null : ` · ${FACT_BASIS_LABEL[fact.basis]}`}
              </dd>
            </div>
          );
        })}
      </dl>
      <p className={styles.summaryNote}>
        {candidate === null
          ? "출처 메모와 편집은 Dataset 상세의 `조사 사실` 섹션에 있습니다."
          : "확정하지 않은 candidate 가 있습니다. Dataset 상세의 `조사 사실` 섹션에서 확인해 주세요."}
      </p>
    </>
  );
}
